// src/pages/Users/Users.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonOff as DeactivateIcon,
  PersonAdd as ActivateIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  useGetAllUsersQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useToggleUserStatusMutation,
  useCreateUserMutation,
} from '../../store/api/authApi';
import { USER_ROLES } from '../../utils/constants';

const ROLES_ADMIN_CAN_ASSIGN = [
  { value: 'Presenter', label: 'Presenter' },
  { value: 'StationManager', label: 'Station Manager' },
  { value: 'Influencer', label: 'Influencer' },
  { value: 'NLAADMIN', label: 'NLA Admin' },
  { value: 'AdminView', label: 'Admin View' },
];

const ROLES_SUPER_ADMIN_CAN_ASSIGN = [
  ...ROLES_ADMIN_CAN_ASSIGN,
  { value: 'Admin', label: 'Admin' },
  { value: 'SuperAdmin', label: 'Super Admin' },
];

const formatGridDate = (value, emptyLabel = '') => {
  if (value == null || value === '') return emptyLabel;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? emptyLabel : date.toLocaleDateString();
};

const apiErrorMessage = (error, fallback) =>
  error?.data?.errors?.[0]?.msg || error?.data?.message || fallback;

const Users = () => {
  const currentRole = useSelector((state) => state.auth.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const canAssignPrivilegedRoles = currentRole === USER_ROLES.SUPER_ADMIN;
  const roleOptions = canAssignPrivilegedRoles
    ? ROLES_SUPER_ADMIN_CAN_ASSIGN
    : ROLES_ADMIN_CAN_ASSIGN;
  // State management
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [filters, setFilters] = useState({
    role: '',
    isActive: '',
    search: '',
  });
  const [editDialog, setEditDialog] = useState({
    open: false,
    user: null,
    isCreate: false,
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    user: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // API hooks
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useGetAllUsersQuery({
    page: paginationModel.page + 1, // API expects 1-based pagination
    limit: paginationModel.pageSize,
    ...filters,
  });

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [toggleStatus, { isLoading: isToggling }] = useToggleUserStatusMutation();
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();

  // Transform data for DataGrid
  const rows = usersData?.users?.map((user) => ({
    id: user._id,
    username: user.username,
    email: user.email,
    role: user.role,
    organisation: user.organization,
    country: user.country,
    stationName: user.stationName,
    isActive: user.isActive,
    status: user.isActive ? 'Active' : 'Inactive',
    createdAt: new Date(user.createdAt),
    lastLogin: user.lastLogin ? new Date(user.lastLogin) : null,
    serviceCode: user.serviceCode,
  })) || [];

  const rowCount = usersData?.pagination?.total || 0;

  // Event handlers
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page
  }, []);

  const handleEditUser = (user) => {
    setEditDialog({
      open: true,
      user,
      isCreate: false,
    });
  };

  const handleCreateUser = useCallback(() => {
    setEditDialog({
      open: true,
      user: null,
      isCreate: true,
    });
  }, []);

  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    handleCreateUser();
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, handleCreateUser]);

  const handleDeleteUser = (user) => {
    setDeleteDialog({
      open: true,
      user,
    });
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      await toggleStatus(userId).unwrap();
      setSnackbar({
        open: true,
        message: 'User status updated successfully',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(error, 'Failed to update user status'),
        severity: 'error',
      });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteUser(deleteDialog.user.id).unwrap();
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success',
      });
      setDeleteDialog({ open: false, user: null });
    } catch (error) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(error, 'Failed to delete user'),
        severity: 'error',
      });
    }
  };

  // Custom toolbar component
  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Button
        startIcon={<AddIcon />}
        onClick={handleCreateUser}
        variant="contained"
        size="small"
        sx={{ mr: 1 }}
      >
        Add User
      </Button>
      <IconButton onClick={refetch} size="small" title="Refresh">
        <RefreshIcon />
      </IconButton>
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );

  // DataGrid columns
  const columns = [
    { 
      field: 'username', 
      headerName: 'Username', 
      width: 150,
      filterable: true,
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      width: 200,
      filterable: true,
    },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small"
          color={
            params.value === 'Admin' || params.value === 'NLAADMIN' || params.value === 'SuperAdmin' ? 'error' :
            params.value === 'StationManager' ? 'warning' :
            params.value === 'AdminView' ? 'info' : 'default'
          }
        />
      ),
    },
    { 
      field: 'organisation', 
      headerName: 'Organisation', 
      width: 150,
      filterable: true,
    },
    { 
      field: 'country', 
      headerName: 'Country', 
      width: 120,
    },
    { 
      field: 'stationName', 
      headerName: 'Station', 
      width: 120,
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small"
          color={params.value === 'Active' ? 'success' : 'default'}
        />
      ),
    },
    { 
      field: 'createdAt', 
      headerName: 'Created', 
      width: 150, 
      type: 'dateTime',
      valueFormatter: (value) => formatGridDate(value),
    },
    { 
      field: 'lastLogin', 
      headerName: 'Last Login', 
      width: 150, 
      type: 'dateTime',
      valueFormatter: (value) => formatGridDate(value, 'Never'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        const isPrivilegedAccount = params.row.role === 'Admin' || params.row.role === 'SuperAdmin';
        if (!canAssignPrivilegedRoles && isPrivilegedAccount) {
          return [];
        }
        return [
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit User">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          onClick={() => handleEditUser(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title={params.row.isActive ? "Deactivate User" : "Activate User"}>
              {params.row.isActive ? <DeactivateIcon /> : <ActivateIcon />}
            </Tooltip>
          }
          label={params.row.isActive ? "Deactivate" : "Activate"}
          onClick={() => handleToggleUserStatus(params.row.id)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete User">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDeleteUser(params.row)}
          showInMenu
        />,
        ];
      },
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage system users and their permissions
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
          variant="contained"
        >
          Add User
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h5">
                {rowCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="h5" color="success.main">
                {rows.filter(row => row.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Inactive Users
              </Typography>
              <Typography variant="h5" color="error.main">
                {rows.filter(row => !row.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search users..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            size="small"
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={filters.role}
              label="Role"
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="AdminView">Admin View</MenuItem>
              <MenuItem value="SuperAdmin">Super Admin</MenuItem>
              <MenuItem value="NLAADMIN">NLA Admin</MenuItem>
              <MenuItem value="StationManager">Station Manager</MenuItem>
              <MenuItem value="Presenter">Presenter</MenuItem>
              <MenuItem value="Influencer">Influencer</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.isActive}
              label="Status"
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiErrorMessage(error, 'Failed to load users')}
        </Alert>
      )}

      {/* DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          rowCount={rowCount}
          loading={isLoading || isUpdating || isDeleting || isToggling}
          paginationMode="server"
          disableRowSelectionOnClick
          showToolbar
          slots={{
            toolbar: CustomToolbar,
          }}
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      {/* Edit/Create User Dialog */}
      <UserEditDialog
        open={editDialog.open}
        user={editDialog.user}
        isCreate={editDialog.isCreate}
        roleOptions={roleOptions}
        onClose={() => setEditDialog({ open: false, user: null, isCreate: false })}
        onSave={async (userData) => {
          const stationName = String(userData.stationName || '').trim();
          const payload = {
            ...userData,
            stationName: stationName || undefined,
          };
          try {
            if (editDialog.isCreate) {
              await createUser(payload).unwrap();
              setSnackbar({
                open: true,
                message: 'User created successfully',
                severity: 'success',
              });
            } else {
              await updateUser({
                userId: editDialog.user.id,
                updateData: payload,
              }).unwrap();
              setSnackbar({
                open: true,
                message: 'User updated successfully',
                severity: 'success',
              });
            }
            setEditDialog({ open: false, user: null, isCreate: false });
          } catch (error) {
            setSnackbar({
              open: true,
              message: apiErrorMessage(error, 'Operation failed'),
              severity: 'error',
            });
          }
        }}
        isLoading={isCreating || isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete user "{deleteDialog.user?.username}"? 
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// User Edit Dialog Component
const UserEditDialog = ({ open, user, isCreate, roleOptions, onClose, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    serviceCode: '',
    organisation: '',
    country: '',
    stationName: '',
    isActive: true,
  });

  React.useEffect(() => {
    if (user && !isCreate) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Never pre-fill password
        role: user.role || '',
        serviceCode: user.serviceCode || '',
        organisation: user.organisation || '',
        country: user.country || '',
        stationName: user.stationName || '',
        isActive: user.isActive ?? true,
      });
    } else if (isCreate) {
      setFormData({
        username: '',
        email: '',
        password: '',
        role: '',
        serviceCode: '',
        organisation: '',
        country: '',
        stationName: '',
        isActive: true,
      });
    }
  }, [user, isCreate, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isCreate ? 'Create New User' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={isCreate ? 'Password' : 'New password'}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={isCreate}
                helperText={
                  isCreate
                    ? 'Password must be at least 6 characters with uppercase, lowercase, and a number'
                    : 'Leave blank to keep the current password. A new password needs 6+ characters with uppercase, lowercase, and a number.'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service Code"
                value={formData.serviceCode}
                onChange={(e) => setFormData({ ...formData, serviceCode: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Organisation"
                value={formData.organisation}
                onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Station Name (Optional)"
                value={formData.stationName}
                onChange={(e) => setFormData({ ...formData, stationName: e.target.value })}
              />
            </Grid>
            {!isCreate && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.isActive}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isCreate ? 'Create User' : 'Update User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default Users;

// // src/pages/Users/Users.jsx
// import React from 'react';
// import { Box, Typography, Paper } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';

// const Users = () => {
//   const [loading, setLoading] = React.useState(false);
//   const [rows, setRows] = React.useState([]);
//   const [rowCount, setRowCount] = React.useState(0);
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: 25,
//   });

//   const columns = [
//     { field: 'id', headerName: 'ID', width: 90 },
//     { field: 'username', headerName: 'Username', width: 150 },
//     { field: 'email', headerName: 'Email', width: 200 },
//     { field: 'role', headerName: 'Role', width: 120 },
//     { field: 'organisation', headerName: 'Organisation', width: 150 },
//     { field: 'status', headerName: 'Status', width: 120 },
//     { field: 'createdAt', headerName: 'Created', width: 150, type: 'dateTime' },
//   ];

//   return (
//     <Box>
//       <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//         User Management
//       </Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//         Manage system users and their permissions
//       </Typography>

//       <Paper sx={{ height: 600, width: '100%' }}>
//         <DataGrid
//           rows={rows}
//           columns={columns}
//           paginationModel={paginationModel}
//           onPaginationModelChange={setPaginationModel}
//           pageSizeOptions={[25, 50, 100]}
//           rowCount={rowCount}
//           loading={loading}
//           paginationMode="server"
//           sortingMode="server"
//           filterMode="server"
//           disableRowSelectionOnClick
//         />
//       </Paper>
//     </Box>
//   );
// };

// export default Users;
